import {
  Callout,
  Chip,
  Empty,
  Loading,
  Panel,
  TableScroll,
  formatDate,
  useLoad,
  SortTh,
  Sorted,
  sortableCell,
} from '@affdice/portal-kit';
import {
  PlatformApi,
} from '../../../client.ts';
import {
  lifetimePolicyLabel,
  referralOverrideCell,
  referralProgrammeSummary,
} from '../../../screens.ts';
import { t } from '../../../messages.ts';
import { Failure } from '../../shared.tsx';

/**
 * §8.9 / §12.3 — this operator's sub-affiliate network, read-only.
 *
 * Everything past "a referral edge exists" — recruiting, a recruiter's own
 * rate, the clawback cascade — is the operator's and the affiliate's own to
 * work; the owner's decision for this batch was that AMS only reads the
 * network (§12.5's "who does what"), the same boundary §3.9d already draws
 * around a brand's commercial terms. So there is no write here, not even a
 * proposal — proposing implies a decision support is positioned to make, and
 * a recruiter's rate is not one.
 *
 * Click-to-load like the panels around it — §3.9c logs per access.
 */
export function OperatorReferralsPanel({ api, operatorId, shown, onShow }: {
  api: PlatformApi;
  operatorId: string;
  shown: boolean;
  onShow: () => void;
}) {
  // See OperatorAffiliatesPanel — the guard has to be in the loader.
  const loaded = useLoad(
    () => (shown ? api.operatorReferrals(operatorId) : Promise.resolve(null)),
    [operatorId, shown],
  );

  if (!shown) {
    return (
      <Panel title={t('ops.refs.title')}>
        <Callout>
          {t('ops.refs.note')}
        </Callout>
        <div className="form-actions">
          <button className="primary" onClick={onShow}>{t('ops.refs.view')}</button>
        </div>
      </Panel>
    );
  }

  if (loaded.state === 'error') return <Panel title={t('ops.refs.title')}><Failure error={loaded.error} /></Panel>;
  if (loaded.state === 'loading' || !loaded.value) {
    return <Panel title={t('ops.refs.title')}><Loading rows={2} /></Panel>;
  }

  const { programme, items } = loaded.value;

  return (
    <Panel title={t('ops.refs.title')}>
      <p className="panel-note">{referralProgrammeSummary(programme)}</p>
      {/* Finding 6 — an edge already recruited keeps paying (an override
          survives the programme being switched off, §12.3), so it stays on
          screen with the "Off" summary above it saying why nothing new is
          joining. The empty state is only for "nothing to show at all". */}
      {items.length === 0 ? (
        <Empty>
          {!programme.enabled
            ? t('ops.refs.notOn')
            : t('ops.refs.nobody')}
        </Empty>
      ) : (
        <Sorted rows={items} by={{ recruiter_name: (r) => sortableCell(r.recruiter_name), sub_name: (r) => sortableCell(r.sub_name), depth: (r) => sortableCell(r.depth), override_bps: (r) => sortableCell(r.override_bps), lifetime_policy: (r) => sortableCell(r.lifetime_policy), valid_from: (r) => sortableCell(r.valid_from), valid_to: (r) => sortableCell(r.valid_to), active: (r) => sortableCell(r.active) }}>
          {(ordered, sort) => (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <SortTh sort={sort} col="recruiter_name">{t('ops.refs.recruiter')}</SortTh><SortTh sort={sort} col="sub_name">{t('ops.refs.sub')}</SortTh><SortTh sort={sort} col="depth" align="num">{t('ops.refs.level')}</SortTh>
                <SortTh sort={sort} col="override_bps" align="num">{t('ops.refs.override')}</SortTh><SortTh sort={sort} col="lifetime_policy">{t('ops.refs.lifetime')}</SortTh>
                <SortTh sort={sort} col="valid_from" align="num">{t('ops.refs.from')}</SortTh><SortTh sort={sort} col="valid_to" align="num">{t('ops.refs.until')}</SortTh><SortTh sort={sort} col="active">{t('common.active')}</SortTh>
              </tr>
            </thead>
            <tbody>
              {ordered.map((r) => (
                <tr key={r.id}>
                  <td>{r.recruiter_name}</td>
                  <td>{r.sub_name}</td>
                  <td className="num faint">{r.depth}</td>
                  {/* §12.3 — the basis sits in the same cell as the rate:
                      "5.00%" alone answers a different question depending on
                      whose figure it is a share of. */}
                  <td className="num">{referralOverrideCell(r.override_bps, r.basis)}</td>
                  <td className="faint">{lifetimePolicyLabel(r.lifetime_policy)}</td>
                  <td className="num faint">{formatDate(r.valid_from)}</td>
                  <td className="num faint">{r.valid_to ? formatDate(r.valid_to) : t('ops.refs.ongoing')}</td>
                  <td><Chip tone={r.active ? 'locked' : 'neutral'}>{r.active ? t('common.active') : t('common.inactive')}</Chip></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
          )}
        </Sorted>
      )}
      <Callout>
        {t('ops.refs.readOnly')}
      </Callout>
    </Panel>
  );
}
